import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Mail, Lock, KeyRound, Eye, EyeOff, Smartphone, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { BRAND, formatLoginError } from "../lib/api";
import { Logo } from "../components/Logo";
import { Button, Field, inputCls } from "../components/ui-kit";
import OfflineAccessButton from "../components/OfflineAccessButton";

export default function Login() {
  const { login, verifyTwoFactor } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  // Secondo passaggio (solo account consulente con verifica in due passaggi attiva)
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);

  const enter = (u) => {
    toast.success("Accesso effettuato");
    nav(u.role === "super_admin" ? "/admin" : "/app", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const result = await login(email, password);
      if (result.twoFactorChallenge) {
        // La password non serve più: non resta in memoria durante il secondo passaggio.
        setChallenge(result.twoFactorChallenge); setPassword(""); setCode(""); setUseRecovery(false);
        return;
      }
      enter(result);
    } catch (e2) {
      const m = formatLoginError(e2);
      setErr(m); toast.error(m);
    } finally { setLoading(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const data = await verifyTwoFactor(challenge, code, rememberDevice);
      if (useRecovery) toast.warning(`Codice di recupero usato: ne restano ${data.recovery_codes_left}`);
      enter(data.user);
    } catch (e2) {
      const m = formatLoginError(e2);
      setErr(m); toast.error(m);
      if (e2.response?.data?.detail?.startsWith("Accesso scaduto")) { setChallenge(null); setPassword(""); }
    } finally { setLoading(false); }
  };

  const backToPassword = () => { setChallenge(null); setCode(""); setErr(""); setPassword(""); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Colonna immagine (destra su desktop, nascosta su mobile) */}
      <div className="relative hidden lg:block order-2">
        <img src={BRAND.loginImage} alt="Dottor Dario Feliciano, consulente HACCP" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-navy/80" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="inline-flex items-center gap-2 self-start px-4 py-2 rounded-full border border-white/25 text-xs uppercase tracking-[0.2em]">
            <ShieldCheck className="w-4 h-4 text-gold-bright" /> Portale riservato
          </div>
          <div>
            <h1 className="font-heading text-4xl xl:text-5xl font-bold leading-tight">
              Il tuo Piano di Autocontrollo,<br />
              <span className="text-gold-bright">sempre sotto controllo.</span>
            </h1>
            <p className="mt-5 max-w-md text-white/70 font-body leading-relaxed">
              Temperature, procedure, non conformità e documenti HACCP in un unico spazio istituzionale,
              configurato e approvato dal consulente DF Consulting Group.
            </p>
          </div>
          <div className="text-white/50 text-xs font-body">© 2026 DF Consulting Group — Dott. Dario Feliciano</div>
        </div>
      </div>

      {/* Colonna form */}
      <div className="order-1 flex flex-col bg-white animate-fade-up lg:items-center lg:justify-center lg:p-10">
        {/* Copertina su telefono/tablet: su desktop la foto è nella colonna a destra */}
        <div className="lg:hidden relative h-[clamp(250px,36svh,340px)] shrink-0" data-testid="login-portrait-mobile">
          <img src={BRAND.loginImage} alt="Dottor Dario Feliciano, consulente HACCP" className="absolute inset-0 h-full w-full object-cover object-[50%_6%]" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/40 via-transparent to-navy/95" aria-hidden="true" />
          <div className="absolute bottom-[86px] inset-x-6 text-white">
            <div className="text-[10px] uppercase tracking-[0.14em] text-white/80 font-body">Il tuo consulente HACCP</div>
            <div className="mt-0.5 font-heading text-2xl font-bold leading-tight">Dott. Dario Feliciano</div>
          </div>
        </div>
        <div className="relative -mt-7 flex justify-center rounded-t-[28px] bg-white px-6 pb-6 sm:px-10 lg:mt-0 lg:w-full lg:rounded-none lg:p-0">
        <div className="w-full max-w-md">
          {/* Su telefono il logo è un sigillo a cavallo tra la foto e il modulo */}
          <div className="lg:hidden mb-4 flex flex-col items-center text-center" data-testid="login-seal-mobile" aria-label="DF HACCP — Gestione Digitale">
            <div className="-mt-12 grid h-24 w-24 place-items-center rounded-full bg-white shadow-[0_18px_40px_-12px_rgba(10,31,68,0.45)] ring-4 ring-white">
              <img src={BRAND.symbol} alt="" aria-hidden="true" className="h-[60px] w-auto object-contain" />
            </div>
            <div className="mt-1.5 font-heading text-3xl font-bold leading-none tracking-[-0.025em] text-navy">
              DF <span className="text-gold">HACCP</span>
            </div>
            <div className="mt-1.5 font-body text-[10px] font-medium uppercase tracking-[0.1em] text-slate-500">
              Controllo <span className="text-gold-bright" aria-hidden="true">•</span> Sicurezza <span className="text-gold-bright" aria-hidden="true">•</span> Conformità
            </div>
            <div className="mt-2 h-0.5 w-14 rounded-full bg-[#14C38E]" aria-hidden="true" />
          </div>
          <div className="hidden lg:flex justify-center mb-12"><Logo size="lg" /></div>
          <p className="text-center text-[11px] lg:text-xs uppercase tracking-[0.14em] lg:tracking-[0.25em] text-slate-400 mb-0.5 lg:mb-1 font-body">Accesso area riservata</p>
          <h2 className="text-center font-heading text-2xl lg:text-3xl font-bold leading-tight mb-1">Bentornato.</h2>
          <p className="text-center text-slate-500 text-sm leading-snug mb-1.5 lg:mb-2 font-body">
            Accedi con email e password. Sei un addetto? Usa il tuo PIN.
          </p>
          <p className="mb-4 lg:mb-7 text-center text-xs leading-snug lg:leading-relaxed text-slate-400 font-body">
            Accesso riservato ai clienti con contratto di consulenza attivo. Piattaforma professionale inclusa nel servizio di consulenza DF Consulting.
          </p>

          {challenge ? (
            <form onSubmit={submitCode} className="space-y-3 lg:space-y-4" data-testid="login-2fa-form">
              <Field label={useRecovery ? "Codice di recupero" : "Codice dell'app sul telefono"} htmlFor="two-factor-code"
                hint={useRecovery ? "Uno dei codici salvati all'attivazione, es. ABCDE-FGHJK. Vale una volta sola." : "Le 6 cifre che vedi ora nell'app di autenticazione."}>
                <div className="relative">
                  <Smartphone className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input id="two-factor-code" required autoFocus value={code} onChange={(e) => setCode(e.target.value)}
                    inputMode={useRecovery ? "text" : "numeric"} autoComplete="one-time-code" autoCapitalize={useRecovery ? "characters" : "none"}
                    autoCorrect="off" spellCheck={false} maxLength={useRecovery ? 11 : 6}
                    className={`${inputCls} pl-10 tracking-[0.3em] font-mono`} placeholder={useRecovery ? "XXXXX-XXXXX" : "123456"} data-testid="login-2fa-code" />
                </div>
              </Field>
              <label className="flex items-center gap-2 text-sm text-slate-600 font-body">
                <input type="checkbox" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} className="h-4 w-4 accent-navy" data-testid="login-2fa-remember" />
                Ricorda questo dispositivo per 30 giorni
              </label>
              {err && <p className="text-sm text-haccp-error" data-testid="login-error">{err}</p>}
              <Button type="submit" variant="primary" loading={loading} className="w-full" data-testid="login-2fa-submit">
                <ShieldCheck className="w-4 h-4" /> Verifica e accedi
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button type="button" onClick={backToPassword} className="inline-flex items-center gap-1.5 text-slate-500 hover:text-navy" data-testid="login-2fa-back">
                  <ArrowLeft className="w-4 h-4" /> Indietro
                </button>
                <button type="button" onClick={() => { setUseRecovery((v) => !v); setCode(""); setErr(""); }} className="text-gold hover:text-navy font-medium" data-testid="login-2fa-toggle-recovery">
                  {useRecovery ? "Usa il codice dell'app" : "Non hai il telefono? Usa un codice di recupero"}
                </button>
              </div>
            </form>
          ) : (
          <>
          <form onSubmit={submit} className="space-y-3 lg:space-y-4" data-testid="login-form">
            <Field label="Email" htmlFor="email">
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                  className={`${inputCls} pl-10`} placeholder="nome@azienda.it" data-testid="login-email" />
              </div>
            </Field>
            <Field label="Password" htmlFor="password">
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input id="password" type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                  className={`${inputCls} pl-10 pr-12`} placeholder="••••••••" data-testid="login-password" />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  className="absolute right-1.5 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy"
                  aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                  aria-pressed={showPassword}
                  data-testid="login-toggle-password"
                >
                  {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              </div>
            </Field>
            {err && <p className="text-sm text-haccp-error" data-testid="login-error">{err}</p>}
            <Button type="submit" variant="primary" loading={loading} className="w-full" data-testid="login-submit">
              <ShieldCheck className="w-4 h-4" /> Accedi
            </Button>
          </form>

          <div className="flex items-center justify-between mt-4 lg:mt-5 text-sm">
            <Link to="/forgot" className="text-slate-500 hover:text-navy" data-testid="link-forgot">Password dimenticata?</Link>
            <Link to="/pin" className="inline-flex items-center gap-1.5 text-gold hover:text-navy font-medium" data-testid="link-pin">
              <KeyRound className="w-4 h-4" /> Accesso addetto (PIN)
            </Link>
          </div>
          <OfflineAccessButton />
          </>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
